import { useEffect, useMemo, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import {
  formatDate,
  TablePlaceholder,
  useStoreState,
} from "../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useRoutePath } from "../../../../../util/Routing";
import { PageWrapper } from "../../../../../layout/portalPageLayout";
import { ContentWrapper } from "../../../../../layout/portalPageLayout";
import { TemplatesNav, TemplatesNavWithPadding } from "./TemplatesNav";
import useSWR from "swr";
import {
  AttributeTemplateSchema,
  TemplateProductFormSchema,
} from "../../../../../types/types.PIM";
import type { TemplateLocationState } from "../../../../../types/types";
import { AxiosError } from "axios";
import { TertiaryButtonMediumV2 } from "../../../../../components/Buttons/Buttons";
import { Breadcrumb } from "../../../../../components/ProductsLayout/SharedProductsLayout";
import { LinkUnstyled } from "../../../../../components/LinkUnstyled/LinkUnstyled";
import { Table } from "../../../../../components/Table/Table";
import { ColumnDef } from "@tanstack/react-table";
import { PlusIcon } from "../../../../../components/Icons/Icons";
import { Loader } from "../../../../../components/Loader/Loader";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { spacingTokens } from "../../../../../designTokens";

export function TemplateFormsTab() {
  const [tableData, setTableData] = useState<TemplateProductFormSchema[]>([]);
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();
  const { template_id } = useParams<{ template_id: string }>();

  const location = useLocation();
  const history = useHistory();
  const { adminPath } = useRoutePath();

  const { data: template, error: template_error } = useSWR<
    AttributeTemplateSchema,
    AxiosError
  >(`/v2/tenants/${tenant_id}/pim/templates/${template_id}`);

  const templateDisplayName = getTemplateDisplayName(
    template ?? {},
    t("Template")
  );

  const tableColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        header: t("Form Name"),
        accessorKey: "name",
      },
      {
        header: t("Created By"),
        accessorKey: "created_by",
      },
      {
        header: t("Created On"),
        accessorKey: "created_at",
        cell: ({ row }) => {
          return <span>{formatDate(row.original.created_at) || "--"}</span>;
        },
      },
      {
        header: t("Last Modified By"),
        accessorKey: "modified_by",
      },
      {
        header: t("Last Modified On"),
        accessorKey: "last_modified_at",
        cell: ({ row }) => {
          return <span>{formatDate(row.original.modified_at) || "--"}</span>;
        },
      },
    ],
    [t]
  );

  const Placeholder = (t: TFunction) => (
    <TablePlaceholder
      message={t("No items to show. Click the button below to add new Form.")}
    />
  );

  useEffect(() => {
    if (template?.forms && template.forms.length > 0) {
      setTableData(
        template.forms
          .sort(
            (a, b) =>
              new Date(b.modified_at).getTime() -
              new Date(a.modified_at).getTime()
          )
          .map((form) => ({
            ...form,
            rowURL: `${adminPath}/pim/templates/${template_id}/forms/${form.id}`,
          }))
      );
    }
  }, [template?.forms, template_id, adminPath]);

  if (!template && !template_error) {
    return <Loader isLoading={true} />;
  }
  if (template_error) {
    return (
      <ErrorPlaceholder
        message={t("There was an error loading the template forms")}
      />
    );
  }

  return (
    <PageWrapper>
      <Breadcrumb
        style={{ padding: `${spacingTokens.spacing_xl}`, paddingBottom: 0 }}
      >
        <LinkUnstyled
          to={{
            pathname: location.search.includes("from=dashboard")
              ? `${adminPath}/dashboard`
              : `${adminPath}/pim/templates`,
            state: {
              ...(location.state as TemplateLocationState),
              templateType:
                template?.template_type ??
                (location.state as TemplateLocationState)?.templateType,
            },
          }}
        >
          {location.search.includes("from=dashboard")
            ? t("Dashboard")
            : t("Templates")}
        </LinkUnstyled>
        <div>/</div>
        <div>{templateDisplayName}</div>
      </Breadcrumb>
      {template && (
        <TemplatesNavWithPadding
          style={{ marginBottom: spacingTokens.spacing_xl }}
        >
          <TemplatesNav
            pageTitle={templateDisplayName}
            tabIndex={5}
            templateID={template_id}
            enableCustomForms={template?.enable_custom_forms}
          />
        </TemplatesNavWithPadding>
      )}
      <ContentWrapper style={{ padding: `0 ${spacingTokens.spacing_xl}` }}>
        <Table
          columns={tableColumns}
          data={tableData}
          lastChildleftAlign
          isLoading={false}
          error={undefined}
          Placeholder={Placeholder(t)}
        />
        <TertiaryButtonMediumV2
          style={{ marginTop: spacingTokens.spacing_3xl }}
          onClick={() => {
            history.push(`${adminPath}/pim/templates/${template_id}/forms/new`);
          }}
          title={t("Add Form")}
        >
          <PlusIcon width={20} height={20} /> {t("Add")}
        </TertiaryButtonMediumV2>
      </ContentWrapper>
    </PageWrapper>
  );
}
